import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import { FolderPlus, Zap, BarChart3, Database, Trash2, Grid3X3, Table as TableIcon, Unlock, Upload, AlertTriangle, Loader2 } from 'lucide-react';
import { 
  extractValues, 
  analyzeDocuments, 
  autoPopulateFiles,
  setProposalFile,
  setNoteFile,
  setSecurityFile,
  clearResults,
  clearAllFiles,
  setExtractionResults,
  clearErrors,
  savePreviewResults
} from '../store/slices/documentsSlice';
import { 
  getRecords,
  getLoanDetails,
  getLoanDocuments,
  clearErrors as clearDatabaseErrors,
  clearLoanData
} from '../store/slices/databaseSlice';
import { addNotification } from '../store/slices/uiSlice';
import PanelButton from '../components/PanelButton';
import ImprovedLoadingBar from '../components/ImprovedLoadingBar';
import { colors } from '../theme/colors';
import { store } from '../store';
import FileUpload from '../components/FileUpload';
import AvailableDocuments from '../components/AvailableDocuments';
import ResultsDisplay from '../components/ResultsDisplay';
import ProgressBar from '../components/ProgressBar';
import LoadingSpinner from '../components/LoadingSpinner';
import FileViewer from '../components/FileViewer';
import LoanSelector from '../components/LoanSelector';
import Table from '../components/Table';
import SearchPresets from '../components/SearchPresets';
import ResultsFilterPresets from '../components/ResultsFilterPresets';
import { useFilter } from '../contexts/FilterContext';
import useProgress from '../hooks/useProgress';
import { useDocumentAnalysisDisplay } from '../hooks/useDocumentAnalysisDisplay';
import { debugDocumentAnalysis } from '../utils/debugDocumentAnalysis';
import { keyframes, css } from '@emotion/react';

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const SpinningIcon = styled.div`
  display: inline-flex;
  animation: ${spin} 1s linear infinite;
`;

const slideUp = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const PreviewBanner = styled.div`
  position: fixed;
  bottom: -6px;
  left: 86px;
  right: 40px;
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border: 1px solid #fbbf24;
  border-radius: 8px;
  padding: 0.75rem 2rem 1.2rem 2rem;
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.05);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  transform: translateY(100%);
  opacity: 0;
  ${props => props.shouldAnimate ? css`animation: ${slideUp} 0.5s ease-out forwards;` : ''}
  
  @media (max-width: 768px) {
    flex-direction: column;
    padding: 0.75rem 1rem;
    left: 100px;
    right: 12px;
  }
`;

const PreviewBannerContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const PreviewBannerMessage = styled.div`
  color: #92400e;
  font-size: 0.95rem;
  line-height: 1.5;
  
  strong {
    font-weight: 600;
    display: block;
    margin-bottom: 0.25rem;
  }
`;

const PreviewBannerActions = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
`;

const PreviewBannerButton = styled.button`
  padding: 0.625rem 1.25rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: none;
  
  &.primary {
    background: #3b82f6;
    color: white;
    
    &:hover:not(:disabled) {
      background: #2563eb;
    }
    
    &:disabled {
      background: #9ca3af;
      cursor: not-allowed;
      opacity: 0.6;
    }
  }
  
  &.secondary {
    background: white;
    color: #374151;
    border: 1px solid #d1d5db;
    
    &:hover:not(:disabled) {
      background: #f9fafb;
      border-color: #9ca3af;
    }
  }
`;

const PageContainer = styled.div`
  background: white;
  border-radius: 8px;
  margin: 1rem;
  min-height: 400px;
  padding-bottom: ${props => props.hasPreviewBanner ? '120px' : '2rem'};
  position: relative;
  z-index: 1;
`;

const ContentWrapper = styled.div`
  padding: 1.5rem;
`;

const CompactButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border: none;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  ${props => props.variant === 'primary' && `
    background: ${colors.primary};
    color: white;
    
    &:hover {
      background: ${colors.primaryHover};
    }
  `}
  
  ${props => props.variant === 'toggle' && `
    background: #9ca3af;
    color: white;
    
    &:hover {
      background: #6b7280;
    }
  `}
  
  ${props => props.variant === 'secondary' && `
    background: white;
    color: #374151;
    border: 1px solid #d1d5db;
    
    &:hover {
      background: #f9fafb;
      border-color: #9ca3af;
    }
  `}
  
  ${props => props.variant === 'success' && `
    background: white;
    color: #374151;
    border: 1px solid #d1d5db;
    
    &:hover {
      background: #f9fafb;
      border-color: #9ca3af;
    }
  `}
  
  ${props => props.variant === 'danger' && `
    background: white;
    color: #374151;
    border: 1px solid #d1d5db;
    
    &:hover {
      background: #f9fafb;
      border-color: #9ca3af;
    }
  `}
  
  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const CompactButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
  flex-wrap: wrap;
`;

const Title = styled.h2`
  margin: 0 0 2rem 0;
  color: #2c3e50;
  font-size: 1.8rem;
`;

const Section = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  margin: 0 0 1rem 0;
  color: #34495e;
  font-size: 1.2rem;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  color: #6b7280;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: #f3f4f6;
    color: #3b82f6;
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin: 2rem 0;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  ${props => {
    if (props.primary) {
      return `
        background-color: #3b82f6;
        color: white;
        &:hover {
          background-color: #2563eb;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }
      `;
    }
    if (props.secondary) {
      return `
        background-color: #95a5a6;
        color: white;
        &:hover {
          background-color: #7f8c8d;
        }
      `;
    }
    return `
      background-color: #ecf0f1;
      color: #2c3e50;
      &:hover {
        background-color: #d5dbdb;
      }
    `;
  }}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background-color: #e74c3c;
  color: white;
  padding: 1rem;
  border-radius: 4px;
  margin: 1rem 0;
`;

const LoanSelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  text-align: center;
  padding: 2rem;
`;

const SelectorTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
`;

const SelectorSubtitle = styled.p`
  color: #6b7280;
  margin-bottom: 2rem;
  font-size: 1rem;
`;


const SuccessMessage = styled.div`
  background-color: #f3f4f6;
  color: #6b7280;
  padding: 1rem;
  border-radius: 4px;
  margin: 1rem 0;
`;



// Component that uses FilterContext for search presets
function GridWithPresets({ extractionResults, analysisResults, loanId }) {
  const { activeFilter, setActiveFilter } = useFilter();
  
  // Determine which results to show (analysis takes priority over extraction)
  const resultsToShow = analysisResults && Object.keys(analysisResults).length > 0 
    ? analysisResults 
    : extractionResults;
  
  return (
    <>
      {/* Decoupled Filter Presets */}
      <ResultsFilterPresets
        data={resultsToShow}
        activeFilter={activeFilter}
        onFilterChange={setActiveFilter}
      />
      
      <ResultsDisplay 
        extractionResults={extractionResults}
        analysisResults={analysisResults}
        loanId={loanId}
      />
    </>
  );
}

function DocumentAnalysis() {
  const dispatch = useDispatch();
  const { loanId } = useParams();
  const navigate = useNavigate();
  const {
    proposalFile,
    noteFile,
    securityFile,
    additionalFiles,
    archiveFiles,
    checklistFiles,
    extractionResults,
    analysisResults,
    activeLoanId,
    loading,
    errors
  } = useSelector(state => state.documents);
  
  const { loading: databaseLoading, errors: databaseErrors, loanDetails, loanDocuments } = useSelector(state => state.database);
  
  // CRITICAL: Initialize to true if we have a loanId to prevent redirect race condition
  // This ensures the first render doesn't trigger a redirect before data loads
  const [isLoadingFromDatabase, setIsLoadingFromDatabase] = useState(!!loanId);
  const [showPreview, setShowPreview] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [directoryPath, setDirectoryPath] = useState('');
  const hasLoadedInitialData = useRef(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'
  const { 
    progress, 
    status, 
    steps, 
    currentStep, 
    isActive, 
    isAnimating,
    resetProgress, 
    startProgress,
    completeProgress, 
    updateProgress
  } = useProgress(() => {
    // This callback is called when progress completes
    // Apply any pending extraction results
    const pendingResults = sessionStorage.getItem('pendingExtractionResults');
    if (pendingResults) {
      try {
        const results = JSON.parse(pendingResults);
        dispatch(setExtractionResults(results));
        sessionStorage.removeItem('pendingExtractionResults');
        
        dispatch(addNotification({
          type: 'success',
          message: `✅ Re-extraction complete! ${Object.keys(results).filter(k => !k.startsWith('_')).length} fields extracted. Review and save if satisfied.`,
          duration: 6000
        }));
      } catch (e) {
        console.error('Error applying pending results:', e);
      }
    }
  });

  // Search presets state - now managed by FilterContext

  // Search presets are now handled by the decoupled ResultsFilterPresets component




  const handleAnalyzeDocuments = () => {
    const files = {
      masterFile,
      proposalFile,
      noteFile,
      securityFile
    };
    dispatch(analyzeDocuments(files));
  };


  const handleClearResults = () => {
    dispatch(clearResults());
    dispatch(clearErrors());
  };

  const handleCancelPreview = () => {
    dispatch(clearResults());
    dispatch(setExtractionResults(null));
    dispatch(clearErrors());
    dispatch(addNotification({
      type: 'info',
      message: 'Preview results cleared',
      duration: 3000
    }));
  };

  // Check if we're in preview mode (results not saved to database)
  const isPreviewMode = extractionResults && (
    extractionResults.saved_to_database === false || 
    extractionResults.preview_mode === true
  );

  // State to control banner slide-in animation (delay 1.5 seconds after results appear)
  const [showBanner, setShowBanner] = useState(false);

  const handleSaveToDatabase = () => {
    const resultsToSave = analysisResults || extractionResults;
    
    if (resultsToSave && loanId) {
      // Use the correct save function - savePreviewResults (same as ActionMenu)
      const matchedResults = resultsToSave.match_result || resultsToSave.extraction_result || resultsToSave;
      
      dispatch(savePreviewResults({ matchedResults, loanId })).then((result) => {
        if (result.type.endsWith('/fulfilled')) {
          dispatch(addNotification({
            type: 'success',
            message: `✅ Successfully saved ${result.payload.saved_fields} fields to database!`,
            duration: 5000
          }));
          
          // Refresh the loan data to show the saved results
          if (loanId) {
            // Remove ?extraction=true query parameter since we're no longer in extraction preview mode
            const url = new URL(window.location);
            if (url.searchParams.has('extraction')) {
              url.searchParams.delete('extraction');
              window.history.replaceState({}, '', url.toString());
            }
            
            // Refresh data from database
            loadLoanData(true).then(() => {
              dispatch(addNotification({
                type: 'info',
                message: '🔄 Data refreshed from database to confirm save',
                duration: 3000
              }));
            }).catch((error) => {
              dispatch(addNotification({
                type: 'warning',
                message: '⚠️ Save successful but failed to refresh data. Please refresh the page to see updated results.',
                duration: 6000
              }));
            });
          }
        } else {
          dispatch(addNotification({
            type: 'error',
            message: '❌ Failed to save data to database. Please try again.',
            duration: 6000
          }));
        }
      }).catch((error) => {
        dispatch(addNotification({
          type: 'error',
          message: '❌ Error saving to database: ' + (error.message || 'Unknown error'),
          duration: 6000
        }));
      });
    } else {
      dispatch(addNotification({
        type: 'warning',
        message: '⚠️ No extraction results to save. Please extract values first.',
        duration: 5000
      }));
    }
  };

  // Handle document preview from S3
  const handlePreviewDocument = async (document) => {
    try {
      // console.log('Preview document:', document);
      // console.log('S3 Key:', document.s3Key);
      
      // Create a file-like object with S3 key for the DocumentPreview component
      const s3File = {
        name: document.name,
        s3Key: document.s3Key,
        type: document.type,
        size: document.size
      };
      
      // Set the preview file to trigger the DocumentPreview component
      setPreviewFile(s3File);
      setShowPreview(true);
      
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: '❌ Failed to preview document: ' + (error.message || 'Unknown error'),
        duration: 5000
      }));
    }
  };

  // Load data from database when loanId is provided
  const loadLoanData = async (forceRefresh = false) => {
    if (!loanId) return;
    
    console.log('🔄 [loadLoanData] Starting to load data for loan:', loanId, 'forceRefresh:', forceRefresh);
    
    // CRITICAL: Set loading state IMMEDIATELY to prevent redirect race condition
    setIsLoadingFromDatabase(true);
    
    // Check if we already have fresh extraction results in Redux state for THIS loan
    // If so, don't load from database - use the fresh results instead
    // BUT if forceRefresh is true (e.g., coming back from fields screen), reload anyway
    const hasFieldData = (extractionResults?.match_result && Object.keys(extractionResults.match_result).length > 0) ||
                        (extractionResults?.extraction_result && Object.keys(extractionResults.extraction_result).length > 0);
    const isCurrentLoan = extractionResults?.loan_id === loanId;
    
    
    if (!forceRefresh && hasFieldData && isCurrentLoan) {
      console.log('🔄 [loadLoanData] Using existing data, skipping database load');
      setIsLoadingFromDatabase(false);
      return;
    }
    
    // Load from database
    dispatch(clearErrors());
    dispatch(clearDatabaseErrors());
    
    try {
      // Load loan details using the new endpoint, then load documents
      const result = await dispatch(getLoanDetails({ loanId, source: 'DocumentAnalysis.jsx:600' }));
      
      if (result.payload && result.payload.data) {
        const loanData = result.payload.data;
        
        // If we have extraction results, populate them in the documents state
        if (loanData.extraction_results) {
          // Set the extraction results in the documents slice so they display in the UI
          dispatch(setExtractionResults(loanData.extraction_results));
        }
      }
      
      // Load documents after loan details completes
      console.log('🔍 [DOCUMENTS DEBUG] Dispatching getLoanDocuments for loanId:', loanId);
      await dispatch(getLoanDocuments({ loanId, source: 'DocumentAnalysis.jsx:634' }));
      
      // CRITICAL: Always set extractionResults and loading state after data loads
      // If we have extraction results, use them; otherwise set empty object to signal "loaded, no data"
      if (result.payload?.data?.extraction_results) {
        // Results already set above at line 621
      } else {
        // No extraction results found - set empty object to signal "loaded, no data"
        console.log('🔄 [loadLoanData] No extraction results in API response - setting to empty object to indicate "loaded, no data"');
        dispatch(setExtractionResults({}));
      }
      
      // CRITICAL: Always set loading to false after data loads (even if no results)
      setIsLoadingFromDatabase(false);
      
    } catch (error) {
      console.error('Error loading loan data:', error);
      // Stop loading on error and set empty results to prevent infinite spinner
      setIsLoadingFromDatabase(false);
      dispatch(setExtractionResults({}));
    }
  };


  // Refresh data when navigating back to this page from fields management
  const location = useLocation();
  const prevLocationRef = useRef(location.pathname);
  
  useEffect(() => {
    const currentPath = location.pathname;
    const prevPath = prevLocationRef.current;
    
    // If we're on a document analysis page and came from system-files (fields management)
    if (currentPath.includes('/document-analysis/') && 
        prevPath.includes('/system-files') && 
        loanId && 
        hasLoadedInitialData.current) {
      
      // Force refresh data when coming back from fields management screen
      // This ensures newly enabled fields are loaded
      loadLoanData(true); // Pass true to force refresh
    }
    
    prevLocationRef.current = currentPath;
  }, [location.pathname, loanId]);
  

  // Clear store when navigating to Document Analysis without loanId (new contract)
  useEffect(() => {
    if (!loanId) {
      // console.log('No loanId provided - clearing store for new contract analysis');
      dispatch(clearResults());
      dispatch(clearErrors());
      dispatch(clearDatabaseErrors());
      dispatch(clearLoanData()); // Clear loan documents and loan details
      dispatch(setExtractionResults(null));
      // Clear ALL uploaded files (including file arrays)
      dispatch(clearAllFiles());
      // Clear directory path
      setDirectoryPath('');
    }
  }, [loanId, dispatch]);

  // Effect to handle loading state when extraction results are available
  // FIX: Stop loading when extractionResults is set (even if empty) OR when API calls complete
  useEffect(() => {
    if (extractionResults !== undefined && extractionResults !== null) {
      setIsLoadingFromDatabase(false);
    }
  }, [extractionResults]);

  // Removed setTimeout fallback - proper state management should handle this

  // Listen for refresh events from ActionMenu save
  useEffect(() => {
    const handleRefreshLoanData = (event) => {
      const { loanId: eventLoanId, forceRefresh } = event.detail;
      if (eventLoanId === loanId) {
        loadLoanData(forceRefresh);
        
        // Remove ?extraction=true query parameter since we're no longer in extraction preview mode
        const url = new URL(window.location);
        if (url.searchParams.has('extraction')) {
          url.searchParams.delete('extraction');
          window.history.replaceState({}, '', url.toString());
        }
      }
    };

    window.addEventListener('refreshLoanData', handleRefreshLoanData);
    return () => {
      window.removeEventListener('refreshLoanData', handleRefreshLoanData);
    };
  }, [loanId, loadLoanData]);



  // Use the custom hook to determine what to display
  const {
    showLoanSelector,
    showUpload,
    showResults,
    showLoading,
    showProgress,
    resultsData,
    hasFiles,
    hasResults,
    currentView,
    saveAndRefresh
  } = useDocumentAnalysisDisplay({
    loanId,
    activeLoanId,
    extractionResults,
    analysisResults,
    isLoadingFromDatabase,
    isActive,
    isAnimating,
    proposalFile,
    noteFile,
    securityFile,
    loadLoanData,
    isPreviewExtractionRunning: loading.previewExtraction,
    isModalOpen: showPreview
  });

  // Banner slide-in animation (delay 1.5 seconds after results appear)
  useEffect(() => {
    if (isPreviewMode && showResults) {
      // Wait 1.5 seconds before showing banner
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 1500);
      return () => clearTimeout(timer);
    } else {
      setShowBanner(false);
    }
  }, [isPreviewMode, showResults]);

  // Debug logging when there are potential issues
  useEffect(() => {
    if (loanId && !hasResults && !isLoadingFromDatabase && !isActive && !isAnimating) {
      console.warn('🚨 POTENTIAL REDIRECT ISSUE - Running diagnostics...');
      debugDocumentAnalysis.diagnose({
        loanId,
        extractionResults,
        analysisResults,
        isLoadingFromDatabase,
        isActive,
        isAnimating,
        proposalFile,
        noteFile,
        securityFile,
        hasResults
      });
    }
  }, [loanId, hasResults, isLoadingFromDatabase, isActive, isAnimating, extractionResults, analysisResults, proposalFile, noteFile, securityFile]);

  // Debug logging removed

  
  
  
  // Dynamically group documents by their fileType from the database
  const documentsByType = useMemo(() => {
    console.log('🔍 [DOCUMENTS DEBUG] loanDocuments:', loanDocuments);
    const grouped = {};
    loanDocuments.forEach(doc => {
      const fileType = doc.fileType;
      if (!grouped[fileType]) {
        grouped[fileType] = [];
      }
      grouped[fileType].push(doc);
    });
    console.log('🔍 [DOCUMENTS DEBUG] documentsByType:', grouped);
    return grouped;
  }, [loanDocuments]);
  
  const hasAvailableDocuments = loanDocuments.length > 0;
  
  
  // Note: Progress completion is now handled by WebSocket messages only
  // The fallback mechanism was causing premature completion

  // If no loan ID is provided, show loan selector
  if (showLoanSelector) {
    return (
      <PageContainer>
        <ContentWrapper>
          <Title>Document Analysis</Title>
          <LoanSelectorContainer>
            <SelectorTitle>Select a Loan</SelectorTitle>
            <SelectorSubtitle>
              Choose a loan ID to view and analyze its documents
            </SelectorSubtitle>
            <LoanSelector />
            
            <div style={{ marginTop: '3rem', textAlign: 'center' }}>
              <div style={{ 
                marginBottom: '1.5rem', 
                color: '#6b7280', 
                fontSize: '0.95rem',
                lineHeight: '1.5'
              }}>
                <strong>Need to create a new case?</strong><br />
                Use the case list to start a new loan review.
              </div>
              {/* NOTE: CreateNewLoan deprecated - navigate to case list instead */}
              <CompactButton 
                variant="primary"
                onClick={() => navigate('/case-list')}
                style={{
                  padding: '1rem 2rem',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  minWidth: '200px'
                }}
              >
                <FolderPlus size={18} />
                Add New Case
              </CompactButton>
            </div>
          </LoanSelectorContainer>
        </ContentWrapper>
      </PageContainer>
    );
  }

  return (
    <PageContainer hasPreviewBanner={isPreviewMode && showResults}>
      <ContentWrapper>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <Title style={{ margin: 0 }}>
            Document Analysis - {loanId} 
            <span style={{ 
              marginLeft: '1rem', 
              fontSize: '0.875rem', 
              fontWeight: '500',
              color: '#3b82f6',
              cursor: 'pointer',
              textDecoration: 'underline'
            }} onClick={() => navigate(`/system-files/${loanId}`)}>
              Core Data
            </span>
          </Title>
          
          {/* Temporarily hidden - can be added back later */}
          {/* <CompactButton 
            variant="secondary"
            data-cy="case-history-button"
            onClick={() => navigate(`/history/${loanId}`)}
          >
            <BarChart3 size={16} />
            Case History
          </CompactButton> */}
        </div>
      

      {errors.extract && (
        <ErrorMessage>Extraction Error: {errors.extract}</ErrorMessage>
      )}
      
      {errors.analyze && (
        <ErrorMessage>Analysis Error: {errors.analyze}</ErrorMessage>
      )}

      {databaseErrors.save && (
        <ErrorMessage>Save Error: {databaseErrors.save}</ErrorMessage>
      )}

      {showLoading && (
        <ImprovedLoadingBar 
          loanId={loanId}
          message="Loading existing data for loan"
        />
      )}

      {showProgress && (
        <Section>
          <ProgressBar 
            progress={progress}
            status={status}
            steps={steps}
            currentStep={currentStep}
          />
        </Section>
      )}
      

        {showResults && resultsData && (
          <Section>
            {viewMode === 'grid' ? (
              <GridWithPresets 
                extractionResults={resultsData}
                analysisResults={analysisResults}
                loanId={loanId}
              />
            ) : (
              <Table 
                data={Object.entries(resultsData || {}).map(([fieldName, data]) => ({
                  field: fieldName,
                  documentValue: data.value || 'Not Found',
                  masterValue: data.master_value || 'Not Found',
                  source: data.source || 'N/A',
                  page: data.page || 'N/A',
                  documentDate: data.document_date || 'N/A',
                  documentMatch: data.document_match || 'N/A',
                  systemDocumentMatch: data.system_document_match || 'N/A'
                }))}
                columns={[
                  { key: 'field', title: 'Field', sortable: true, width: '200px' },
                  { key: 'documentValue', title: 'Document Value', sortable: true, width: '150px' },
                  { key: 'masterValue', title: 'Master Value', sortable: true, width: '150px' },
                  { key: 'source', title: 'Source', sortable: true, width: '120px' },
                  { key: 'page', title: 'Page', sortable: true, width: '80px' },
                  { key: 'documentDate', title: 'Document Date', sortable: true, width: '120px' },
                  { key: 'documentMatch', title: 'Document Match', sortable: true, width: '120px' },
                  { key: 'systemDocumentMatch', title: 'System Match', sortable: true, width: '120px' }
                ]}
                pageSize={20}
                searchable={true}
                sortable={true}
                pagination={true}
              />
            )}
            
          </Section>
        )}

      {/* Redirecting to scans page for upload functionality */}
      {showUpload && (
        <Section>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <LoadingSpinner />
            <p style={{ marginTop: '1rem', color: '#6b7280' }}>
              Redirecting to upload page...
            </p>
          </div>
        </Section>
      )}
      </ContentWrapper>
      
      <FileViewer
        document={previewFile}
        documents={loanDocuments}
        isOpen={showPreview}
        onDocumentSelect={setPreviewFile}
        onClose={() => {
          setShowPreview(false);
          setPreviewFile(null);
        }}
      />

      {/* Preview Mode Banner */}
      {isPreviewMode && showResults && (
        <PreviewBanner shouldAnimate={showBanner}>
          <PreviewBannerContent>
            <AlertTriangle size={32} style={{ color: '#f59e0b', flexShrink: 0 }} />
            <PreviewBannerMessage>
              <strong>Preview Results</strong>
              <span>Save to database to enable editing, or cancel to clear these preview results.</span>
            </PreviewBannerMessage>
          </PreviewBannerContent>
          <PreviewBannerActions>
            <PreviewBannerButton
              className="secondary"
              onClick={handleCancelPreview}
            >
              Cancel
            </PreviewBannerButton>
            <PreviewBannerButton
              className="primary"
              onClick={handleSaveToDatabase}
              disabled={loading.save || databaseLoading.save}
            >
              {loading.save || databaseLoading.save ? (
                <>
                  <SpinningIcon>
                    <Loader2 size={16} />
                  </SpinningIcon>
                  Saving...
                </>
              ) : (
                <>
                  <Database size={16} />
                  Save to Database
                </>
              )}
            </PreviewBannerButton>
          </PreviewBannerActions>
        </PreviewBanner>
      )}
    </PageContainer>
  );
}

export default DocumentAnalysis;

